/// <amd-module name="Build/Designer/YamlWizardPage" />
import { BuildDefinitionVariable } from "Build/Client/Contracts/Build";
import { CommitFileInfo } from "Build/Client/Contracts/DataProvider";
import { IBuildLinkingService } from "Build/Client/Linking";
import { RepositoryDescriptor } from "Build/Common/Library/Types";
import { FileType, IFileListItem } from "Build/Designer/Components/FileList";
import { EditorConstants } from "Build/Designer/Constants";
import { SaveResult } from "Build/Common/Library/Designer.types";
import { CreateAndRunPipelineResult, IPipelineService, PipelineInfo } from "Build/Common/Library/PipelineService";
import { PipelineVariablesPanel } from "Build/Designer/PipelineVariablesPanel";
import * as Resources from "Build/Designer/Resources";
import { SavePanel } from "Build/Designer/SavePanel";
import { FileAsset, WizardState } from "Build/Designer/Wizard.types";
import { CommitDescriptor, IYamlFileService } from "Build/Designer/YamlFileService";
import { YamlTab } from "Build/Designer/YamlTab";
import { ISourceProvider, RepositoryTypes } from "Build/SourceProviders/Common";
import { ISourceProviderService } from "Build/SourceProviders/SourceProvider";
import * as React from "react";
import { ITfsPageService } from "Tfs/Platform/Page";
import { EmptyGuidString, endsWith, equals, format } from "VSS/Core/Util/String";
import { ContributionServiceEvent, IDataProviderQueryCompletedEvent, IServerException, IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { onClickFPS } from "VSS/Platform/FPS";
import { INavigationStateChangedEvent, IVssHistoryService } from "VSS/Platform/History";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { ButtonGroup } from "VSSUI/ButtonGroup";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Spinner, SpinnerOrientation } from "VSSUI/Spinner";
import { SplitButton } from "VSSUI/SplitButton";
import { css } from "VSSUI/Util";

interface Props {
    wizardState: WizardState;
    content: string | undefined;
    path: string;
    url: string | undefined;
    onAction: (action: string, additionalTelemetry?: { [key: string]: any }) => void;
    onHistoryChanged: (value: INavigationStateChangedEvent) => void;
    contentSha: string;
    redirectToPipeline: (pipelineUrl: string) => void;
    additionalFiles?: FileAsset[];
}

interface State {
    content: string;
    fileName: string;
    sourceBranch: string;
    isEditorReady: boolean;
    savePanelIsOpen: boolean;
    isSaving: boolean;
    saveErrorMessage: string;
    errorMessage: string;
    variables: { [key: string]: BuildDefinitionVariable };
    variablesPanelIsOpen: boolean;
    isRunning: boolean;
    pipelineCreated?: PipelineInfo;
    savedFileInfo?: SaveResult;
}

/**
 * YAML Editor page of the CI Design wizard.
 */
export class YamlWizardPage extends VssComponent<Props, State, IVssComponentContext> {
    private _editor: any /*IEditor*/;
    private _editorIsReadOnly: boolean;
    private _lastSavedContent: string;
    private _lastSavedSha: string;
    private _lastSavedFileName: string;
    private _isVariablesPanelEnabled: boolean;
    private _runAfterSaving: boolean;

    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);
        this._editorIsReadOnly = false;
        this._lastSavedContent = props.content || "";
        this._lastSavedSha = props.contentSha || "";
        this._lastSavedFileName = this._trimPath(this.props.path);
        this._runAfterSaving = true;

        this._isVariablesPanelEnabled = !isFeatureFlagEnabled(context.pageContext, "Build2.DisableYamlEditorVariables", false);

        this.state = {
            content: this._lastSavedContent,
            fileName: this._lastSavedFileName,
            sourceBranch: "",
            isEditorReady: false,
            isRunning: false,
            savePanelIsOpen: false,
            isSaving: false,
            saveErrorMessage: "",
            errorMessage: "",
            variables: {},
            variablesPanelIsOpen: false,
            pipelineCreated: undefined,
            savedFileInfo: undefined
        };
    }

    public componentDidMount() {
        window.addEventListener("beforeunload", this._navigateAwayHandler);
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService.subscribe(this._onDataProviderQueryEnd, ContributionServiceEvent.DataProviderQueryCompleted);

        this._switchHistoryChangeHandler(this.props.onHistoryChanged, this._onHistoryChanged);
    }

    public componentWillUnmount(): void {
        window.removeEventListener("beforeunload", this._navigateAwayHandler);

        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        historyService.unsubscribe(this._onHistoryChanged);

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService.unsubscribe(this._onDataProviderQueryEnd, ContributionServiceEvent.DataProviderQueryCompleted);
    }

    public render(): JSX.Element {
        const sourceProvider = this._getSourceProvider();
        const canCreatePr = !equals(this.props.wizardState.sourceProvider, RepositoryTypes.Bitbucket, true)
            || isFeatureFlagEnabled(this.context.pageContext, "Build2.BitbucketAzurePipelinesOAuthClient", false);

        return (
            <div className="ci-yaml-wizard-view flex-column flex-grow">
                {this._renderHeader()}
                {
                    this.state.errorMessage && this.state.errorMessage !== "" &&
                    <MessageCard className="ci-editor-error" severity={MessageCardSeverity.Error}>
                        {this.state.errorMessage}
                    </MessageCard>
                }
                <YamlTab
                    content={this.state.content}
                    editor={this._editor}
                    onContentUpdated={this._onYamlContentUpdated}
                    onEditorCreated={this._onEditorCreated}
                    sidePanelInitiallyOpen={false}
                    repositoryName={this.props.wizardState.repositoryName}
                    repositoryIconName={sourceProvider && sourceProvider.getRepoIconName()}
                    repositoryUrl=""
                    branchName=""
                    fileName={this.state.fileName}
                    fileUrl=""
                    supportFPS={equals(this.props.wizardState.sourceProvider, RepositoryTypes.TfsGit, true)}
                    isYamlDirty={this._isDirty() || this._isNewFile()}
                    definition={undefined}
                    onBranchSelected={() => { }}
                    onFileNameChanged={this._onFileNameChanged} />

                {this.state.variablesPanelIsOpen &&
                    <PipelineVariablesPanel
                        variables={this.state.variables}
                        isSaving={false}
                        onSave={this._onSavePipelineVariables}
                        onDismiss={this._dismissPipelineVariablesPanel} />
                }

                <SavePanel
                    errorMessage={this.state.saveErrorMessage}
                    fileName={this.state.fileName}
                    defaultBranch={this.props.wizardState.branch || Resources.DefaultBranch}
                    isOpen={this.state.savePanelIsOpen}
                    isSaving={this.state.isSaving}
                    isRunning={this.state.isRunning}
                    saveAndRun={this._runAfterSaving}
                    canCreatePr={canCreatePr}
                    onSave={this._saveYamlAndPipeline}
                    onDismiss={this._dismissSavePanel}
                    filesAdded={this.props.additionalFiles &&
                        [
                            {
                                name: this.state.fileName,
                                description: Resources.YamlFileDescription,
                                type: FileType.Yaml,
                            },
                            ...this._mapToFileListItems(this.props.additionalFiles)
                        ]}
                />
            </div>
        );
    }

    private _renderHeader = (): JSX.Element => {
        const yamlRequiresSave = this._isDirty() || this._isNewFile();
        return (
            <div className="ci-editor-header header-section flex-row">
                <div className="scroll-hidden">
                    <div className="subheader body-m secondary-text">{Resources.NewPipeline}</div>
                    <div className="title-l text-ellipsis">{Resources.ReviewYourPipelineYAML}</div>
                </div>
                <div className="ci-editor-action-buttons flex-self-end">

                    {this.state.isRunning
                        ? <Spinner
                            className="loading-indicator"
                            orientation={SpinnerOrientation.row}
                            label={Resources.CreatingPipeline} />
                        : <ButtonGroup>
                            {
                                this._isVariablesPanelEnabled &&
                                <Button
                                    className="ci-editor-action-button"
                                    text={Resources.CommandVariables}
                                    primary={false}
                                    disabled={this.state.isRunning}
                                    onClick={this._showPipelineVariablesPanel} />
                            }

                            < SplitButton
                                primary={true}
                                disabled={!this.state.isEditorReady || !this.state.content || this.state.isRunning}
                                buttonProps={{
                                    className: css("ci-editor-action-button", EditorConstants.ElementToFocusOnEscape),
                                    text: yamlRequiresSave ? Resources.SaveAndRunButtonLabel : Resources.RunButtonLabel,
                                    onClick: this._onSaveAndRunClick,
                                }}
                                menuButtonProps={{
                                    contextualMenuProps: {
                                        menuProps: {
                                            id: "saveSubmenu",
                                            items: [{
                                                id: "saveOnly",
                                                text: Resources.SaveButtonLabel,
                                                disabled: this.state.isRunning,
                                                onActivate: this._onSaveOnlyClick,
                                            }]
                                        }
                                    }
                                }} />
                        </ButtonGroup>
                    }
                </div>
            </div>
        );
    }

    private _navigateAwayHandler = (event: BeforeUnloadEvent): void => {
        if (this._isDirty()) {
            event.returnValue = Resources.ConfirmLosingUnsavedChange;
        }
    }

    private _onHistoryChanged = (value: INavigationStateChangedEvent): void => {
        if (!this._isDirty() || confirm(Resources.ConfirmLosingUnsavedChange)) {
            this._switchHistoryChangeHandler(this._onHistoryChanged, this.props.onHistoryChanged);
            this.props.onHistoryChanged(value);
        }
    }

    private _switchHistoryChangeHandler(oldHandler: (value: INavigationStateChangedEvent) => void, newHandler: (value: INavigationStateChangedEvent) => void) {
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        historyService.unsubscribe(oldHandler);
        historyService.subscribe(newHandler);
    }

    private _isDirty(): boolean {
        if (this._editor) {
            return this._editor.getValue() !== this._lastSavedContent;
        }

        return false;
    }

    private _trimPath(path: string) {
        if (path) {
            path = path.startsWith("./") ? path.slice(2) : path;
            path = path.startsWith("/") ? path.slice(1) : path;
        }
        return path;
    }

    private _onEditorCreated = (editor: any /*IEditor*/) => {
        if (editor) {
            this._editor = editor;
            editor.focus();
            this.setState({ isEditorReady: true });
        }
    }

    private _onYamlContentUpdated = (content: string) => {
        if (content !== this.state.content) {
            this.setState({ content });
        }
    }

    private _onFileNameChanged = (fileName: string) => {
        if (fileName !== this.state.fileName) {
            this.setState({ fileName });
        }
    }

    private _isNewFile(newFileName?: string) {
        if (!this._lastSavedSha) {
            return true;
        }
        const fileName = newFileName || this.state.fileName;
        return fileName !== this._lastSavedFileName;
    }

    private _ensureEditorIsReadOnly(readOnly: boolean) {
        if (this._editor && this._editorIsReadOnly !== readOnly) {
            this._editorIsReadOnly = readOnly;
            this._editor.updateOptions({ readOnly })
        }
    }

    private _getSourceProvider(): ISourceProvider {
        const sourceProviderService = this.context.pageContext.getService<ISourceProviderService>("ISourceProviderService");
        return sourceProviderService.getSourceProvider(this.props.wizardState.sourceProvider);
    }

    private _showPipelineVariablesPanel = () => {
        this.setState({ variablesPanelIsOpen: true });
    }

    private _dismissPipelineVariablesPanel = () => {
        this.setState({ variablesPanelIsOpen: false });
    }

    private _onSavePipelineVariables = (variables: { [name: string]: BuildDefinitionVariable }) => {
        this.setState({ variables });
        this._dismissPipelineVariablesPanel();
    }

    private _showSavePanel = () => {
        const content = (this._editor && this._editor.getValue()) || "";
        this.setState({
            content,
            savePanelIsOpen: true,
            isSaving: false
        }, () => this.props.onAction("ShowSavePanel"));
    }

    private _saveAndRunPipeline = () => {
        this._ensureEditorIsReadOnly(true);
        this.setState({
            isRunning: true,
            errorMessage: ""
        });

        const repository: RepositoryDescriptor = {
            sourceProviderType: this.props.wizardState.sourceProvider,
            connectionId: this.props.wizardState.connectionId,
            id: this.props.wizardState.repositoryId,
            name: this.props.wizardState.repositoryName,
            defaultBranch: this.props.wizardState.branch
        };

        const fileBranch = this.state.sourceBranch;
        const fileInfo = this.state.savedFileInfo;
        const filePath = this.state.fileName;
        const pipelinePath = this.props.wizardState.pipelinePath;
        const variables = this.state.variables;
        const createOnly = !this._runAfterSaving;

        let useNewDefaultQueue = isFeatureFlagEnabled(this.context.pageContext, "Build2.NewDefaultQueue", false);
        let defaultQueueName = useNewDefaultQueue ? "Azure Pipelines" : "Hosted Ubuntu 1604";
        const pipelineService = this.context.pageContext.getService<IPipelineService>("IPipelineService");
        pipelineService.createAndRunPipeline(repository, filePath, fileBranch, fileInfo, pipelinePath, variables, createOnly, defaultQueueName)
            .then((result: CreateAndRunPipelineResult | undefined) => {
                if (result) {
                    if (result.pipeline) {
                        const pageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
                        const projectId = pageService.getData()!.project!.id || EmptyGuidString;
                        const projectAndPipelineId = format("{0}:{1}", projectId, result.pipeline.id);

                        this.props.onAction("PipelineCreated", { "ProjectAndPipelineId": projectAndPipelineId });
                        this.props.onAction("Run", { "yamlWasEdited": this._lastSavedContent !== this.props.content });
                        this.setState({ pipelineCreated: result.pipeline });

                        if (createOnly) {
                            this._gotoEmptySummaryPage(result.pipeline.id, fileBranch);
                        }
                    }
                    if (result.pipelineBuildWebUrl) {
                        this.props.redirectToPipeline(result.pipelineBuildWebUrl);
                    }
                    if (result.queuePipelineErrorMessage) {
                        this._handleRunError(result.queuePipelineErrorMessage);;
                    }
                }

                const errorMessage = pipelineService.getPipelineErrorMessage();
                if (errorMessage) {
                    this._handleRunError(errorMessage);
                }
            });
    }

    private _onDataProviderQueryEnd = (queryEndEvent: IDataProviderQueryCompletedEvent) => {
        if (queryEndEvent.dataProviderExceptions) {
            const e: IServerException = queryEndEvent.dataProviderExceptions["ms.vss-build-web.create-and-run-pipeline-data-provider"];
            if (e) {
                this._handleRunError(e.message);
            }
        }
    }

    private _onSaveAndRunClick = () => {
        this._save(true)
    };

    private _onSaveOnlyClick = () => {
        this._save(false);
    }

    private _save = (runAfterSaving: boolean) => {
        this._runAfterSaving = runAfterSaving;
        const yamlRequiresSave = this._isDirty() || this._isNewFile();
        yamlRequiresSave ? this._showSavePanel() : this._saveAndRunPipeline();
    }

    private _saveYamlAndPipeline = (commitDescriptor: CommitDescriptor) => {
        this._ensureEditorIsReadOnly(true);
        this.setState({
            saveErrorMessage: "",
            isSaving: true
        }, () => this.props.onAction(commitDescriptor.createNewBranch ? "SavePR" : "SaveCommit"));
        const content = (this._editor && this._editor.getValue()) || "";

        const repository: RepositoryDescriptor = {
            connectionId: this.props.wizardState.connectionId,
            sourceProviderType: this.props.wizardState.sourceProvider,
            id: this.props.wizardState.repositoryId,
            projectId: this.props.wizardState.repositoryProjectId
        };

        // Add [skip ci] for new pipeline commits to avoid Build auto-creating another pipeline, plus we manually run it anyway.
        commitDescriptor.skipCi = true;

        const yamlFileService = this.context.pageContext.getService<IYamlFileService>("IYamlFileService");
        const contentSha = this._isNewFile() ? "" : this._lastSavedSha;

        if (this.props.additionalFiles) {
            const filesToCommit: CommitFileInfo[] = [
                {
                    path: this.state.fileName,
                    content,
                    oldObjectId: contentSha
                },
                ...this._mapToCommitFileInfo(this.props.additionalFiles)];
            yamlFileService.commitFiles(repository, this.props.wizardState.branch, filesToCommit, commitDescriptor).then((savedFileInfo: SaveResult) => {
                this._onYamlSaved(savedFileInfo, content);
            }, (error: Error) => {
                this._handleSaveError(error.message);
            });
        } else {
            yamlFileService.commitFile(repository, this.props.wizardState.branch, this.state.fileName, content, commitDescriptor, contentSha).then((savedFileInfo: SaveResult) => {
                this._onYamlSaved(savedFileInfo, content);
            }, (error: Error) => {
                this._handleSaveError(error.message);
            });
        }
    }

    private _onYamlSaved = (savedFileInfo: SaveResult, content: string) => {
        this._lastSavedContent = content;
        this._lastSavedSha = savedFileInfo.objectId;
        this._lastSavedFileName = this.state.fileName;
        this.setState({
            isSaving: false,
            sourceBranch: savedFileInfo.branch,
            savedFileInfo: savedFileInfo
        }, () => {
            this._saveAndRunPipeline();
        });
    }

    private _dismissSavePanel = () => {
        if (!this.state.isSaving) {
            this.setState({
                saveErrorMessage: "",
                savePanelIsOpen: false,
            });
        }
    }

    private _handleRunError = (errorMessage: string) => {
        this.setState({
            isRunning: false,
            savePanelIsOpen: false,
            errorMessage
        });
    }

    private _handleSaveError = (errorMessage: string) => {
        this.setState({
            saveErrorMessage: errorMessage,
            isSaving: false,
            isRunning: false,
            savePanelIsOpen: true
        });
        this._ensureEditorIsReadOnly(false);
    }

    private _gotoEmptySummaryPage(pipelineId: number, branch: string) {
        const url = this.buildLinkingService.getPipelinesUrl(pipelineId);
        onClickFPS(this.context.pageContext, url, true);
    }

    private _mapToFileListItems = (fileAssets: FileAsset[]): IFileListItem[] => {
        return fileAssets.map((file) => {
            return {
                name: file.destinationPath,
                description: file.description,
                type: this._getFileType(file.path)
            }
        });
    }

    private _getFileType = (fileName: string): FileType => {
        if (endsWith(fileName, "yml", true)) {
            return FileType.Yaml;
        }
        return FileType.Other;
    }

    private _mapToCommitFileInfo = (fileAssets: FileAsset[]): CommitFileInfo[] => {
        return fileAssets.map((file) => {
            return {
                path: file.destinationPath,
                content: file.content,
                oldObjectId: ""
            }
        });
    }

    private get buildLinkingService(): IBuildLinkingService {
        return this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService")
    }
}
